import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";

import { TEMPLATE_LIBRARY_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import TableSearchInput from "../TableSearchInput";
import AppChip from "../../common/app-chip";
import PlaceholderInput from "./PlaceholderInput";
import MessageNode from "./MessageNode";
import {
  CODE_EXPIRY_OPTIONS,
  MAX_APPS,
  PACKAGE_NAME_MAX,
  SIGNATURE_HASH_LENGTH,
  buildAuthPreview,
  buildSampleFields,
  getAppErrors,
  getBodyVariables,
  normalizeLibrary,
} from "./authTemplateUtils";

const VARIABLE_OPTIONS = ["otp", "name", "email", "phone", "custom"];
const BUSINESS_TERMS_URL = "https://www.whatsapp.com/legal/business-terms";

const DELIVERY_OPTIONS = [
  {
    value: "ZERO_TAP",
    label: "Zero-tap auto-fill",
    description:
      "The code is sent to your app without the customer tapping a button. Falls back to auto-fill or copy code.",
  },
  {
    value: "ONE_TAP",
    label: "One-tap auto-fill",
    description:
      "The code is sent to your app when the customer taps the button. Falls back to copy code.",
  },
  {
    value: "COPY_CODE",
    label: "Copy code",
    description: "The customer copies the code and pastes it into your app.",
  },
];

const errorBoxSx = {
  backgroundColor: "#FFEBEE",
  color: "#D32F2F",
  borderRadius: "8px",
  px: 1.5,
  py: 1,
  ...typography.bodySmall,
};

const linkButtonSx = {
  textTransform: "none",
  color: ColorsConstants.primaryPurple100,
  fontWeight: 600,
  px: 0,
  minWidth: 0,
};

const newApp = () => ({
  id: `${Date.now()}-${Math.random()}`,
  package_name: "",
  signature_hash: "",
});

const HighlightedBody = ({ text }) => (
  <>
    {(text || "").split(/(\{\{[^{}]+\}\})/g).map((part, index) =>
      /^\{\{[^{}]+\}\}$/.test(part) ? (
        <Box
          key={index}
          component="span"
          sx={{
            backgroundColor: ColorsConstants.primaryPurple60,
            color: ColorsConstants.primaryPurple100,
            borderRadius: "4px",
            px: 0.5,
            fontWeight: 500,
          }}
        >
          {part}
        </Box>
      ) : (
        part
      ),
    )}
  </>
);

const AuthTemplateEditor = ({ templateName, language, onChange }) => {
  const { getValidToken } = useAuth();

  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState("");
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState("");

  const [selected, setSelected] = useState(null);
  const [sampleBodyFields, setSampleBodyFields] = useState([]);
  const [addSecurity, setAddSecurity] = useState(true);
  const [expiryEnabled, setExpiryEnabled] = useState(true);
  const [expiryMinutes, setExpiryMinutes] = useState(10);
  const [otpType, setOtpType] = useState("ZERO_TAP");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [apps, setApps] = useState([newApp()]);
  const [touched, setTouched] = useState({});

  const loadLibrary = async () => {
    setLibraryLoading(true);
    setLibraryError("");
    try {
      const token = await getValidToken();
      const response = await axios.get(TEMPLATE_LIBRARY_ENDPOINT, {
        params: { language },
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": localStorage.getItem("tenant"),
        },
        timeout: 30000,
      });
      setLibrary(normalizeLibrary(response.data));
    } catch (err) {
      setLibrary([]);
      setLibraryError(
        err.response?.data?.message || "Couldn't load the template library.",
      );
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    loadLibrary();
  }, [language]);

  const available = library;
  const query = search.trim().toLowerCase();
  const shown = available.filter(
    (t) =>
      !query ||
      t.name.toLowerCase().includes(query) ||
      (t.body || "").toLowerCase().includes(query),
  );

  const selectTemplate = (template) => {
    setSelected(template);
    setSampleBodyFields(
      buildSampleFields(template.body, template.body_param_types, template.body_params),
    );
    setNotice("");
  };

  const reopenPicker = () => {
    setSelected(null);
    setSampleBodyFields([]);
  };

  // After a language change, keep the selection only if the template exists in that language
  useEffect(() => {
    if (!selected || libraryLoading || libraryError || library.includes(selected)) return;
    const match = library.find((t) => t.name === selected.name);
    if (match) {
      selectTemplate(match);
    } else {
      reopenPicker();
      setNotice(
        `This template isn't available in ${language[0].toUpperCase() + language.slice(1)}.`,
      );
    }
  }, [library]);

  useEffect(() => {
    onChange({
      selected,
      sampleBodyFields,
      addSecurity,
      expiryEnabled,
      expiryMinutes,
      otpType,
      termsAccepted,
      apps,
    });
  }, [
    selected,
    sampleBodyFields,
    addSecurity,
    expiryEnabled,
    expiryMinutes,
    otpType,
    termsAccepted,
    apps,
  ]);

  const appErrors = getAppErrors(apps);
  const updateApp = (id, key, value) =>
    setApps(apps.map((app) => (app.id === id ? { ...app, [key]: value } : app)));
  const touchApp = (id, key) =>
    setTouched((prev) => ({ ...prev, [`${id}.${key}`]: true }));

  const otpCount = sampleBodyFields.filter((f) => f.type === "otp").length;
  const hasCodeVariable = sampleBodyFields.some((f) => f.variable_name === "code");

  const renderPicker = () => (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ ...typography.h1 }}>
              Choose a template
            </Typography>
            <Typography variant="body2" sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryGray60 }}>
              Authentication messages use text set by Meta. You can't edit the message, only
              map its variables and choose the options below.
            </Typography>
          </Box>

          {notice && <Box sx={errorBoxSx}>{notice}</Box>}

          {libraryLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : libraryError ? (
            <Stack direction="row" spacing={2} alignItems="center" sx={errorBoxSx}>
              <span style={{ flex: 1 }}>{libraryError}</span>
              <Button size="small" sx={linkButtonSx} onClick={loadLibrary}>
                Retry
              </Button>
            </Stack>
          ) : (
            <>
              <TableSearchInput
                searchQuery={search}
                onSearchChange={(e) => setSearch(e.target.value)}
                placeholder="Search templates by name or text"
              />
              <Typography variant="body2" sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray50 }}>
                Showing {shown.length} of {available.length}
              </Typography>

              {shown.length === 0 ? (
                <Typography variant="body2" sx={{ ...typography.bodyLarge, py: 2, textAlign: "center" }}>
                  {query
                    ? `No templates match "${search.trim()}"`
                    : "No authentication templates are available for this language."}
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {shown.map((template) => {
                    const varCount = getBodyVariables(template.body).length;
                    return (
                      <Grid item xs={12} sm={6} lg={4} key={`${template.name}-${template.language}`}>
                        <Card variant="outlined" sx={{ height: "100%" }}>
                          <CardActionArea
                            onClick={() => selectTemplate(template)}
                            sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
                          >
                            <Box sx={{ backgroundColor: "#ECE5DD", p: 1.5, flex: 1 }}>
                              <Box
                                sx={{
                                  backgroundColor: "#FFFFFF",
                                  borderRadius: "8px",
                                  p: 1,
                                  ...typography.bodySmall,
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                <HighlightedBody text={template.body} />
                              </Box>
                            </Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.5, py: 1 }}>
                              <Typography variant="body2" sx={{ ...typography.bodySmall, fontWeight: 600, wordBreak: "break-all" }}>
                                {template.name}
                              </Typography>
                              <Typography variant="body2" sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray50, whiteSpace: "nowrap", ml: 1 }}>
                                {varCount === 0 ? "No vars" : `${varCount} var${varCount === 1 ? "" : "s"}`}
                              </Typography>
                            </Stack>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderBody = () => (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ ...typography.h1 }}>
                Message body
              </Typography>
              <AppChip label={selected.name} />
            </Stack>
            <Button size="small" sx={linkButtonSx} onClick={reopenPicker}>
              Select template
            </Button>
          </Stack>
          <Box
            sx={{
              backgroundColor: "#F5F5F5",
              borderRadius: "8px",
              p: 1.5,
              ...typography.bodyLarge,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            <HighlightedBody text={selected.body} />
          </Box>

          {sampleBodyFields.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ ...typography.h1, marginBottom: 1 }}>
                Body Variables
              </Typography>
              {!hasCodeVariable && (
                <Typography variant="body2" sx={{ ...typography.bodyLarge, mb: 2, color: ColorsConstants.primaryGray60 }}>
                  One variable is set to OTP code by default. Change it if another variable carries the code.
                </Typography>
              )}
              <Box sx={{ display: "flex", flexWrap: "wrap", width: "100%", gap: 4 }}>
                {sampleBodyFields.map((sample, index) => (
                  <Box key={index} sx={{ marginBottom: "16px", display: "flex", flex: { xs: 1, md: 0.5 } }}>
                    <Typography variant="body2" sx={{ display: "inline-block", mr: 1, whiteSpace: "nowrap" }}>
                      {`{{${sample.variable_name}}}:`}
                    </Typography>
                    <PlaceholderInput
                      sample={sample}
                      sampleBodyFields={sampleBodyFields}
                      index={index}
                      setsampleBodyFields={setSampleBodyFields}
                      options={VARIABLE_OPTIONS}
                      disabled={sample.variable_name === "code"}
                      label="Variable source"
                    />
                  </Box>
                ))}
              </Box>
              {sampleBodyFields.some((f) => !f.type) && (
                <Typography variant="body2" color="error">Map every variable.</Typography>
              )}
              {otpCount !== 1 && (
                <Typography variant="body2" color="error">Map one variable to OTP code.</Typography>
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderContentOptions = () => (
    <Card>
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="h6" sx={{ ...typography.h1 }}>
            Content options
          </Typography>
          <FormControlLabel
            control={<Switch checked={addSecurity} onChange={(e) => setAddSecurity(e.target.checked)} />}
            label={
              <Typography sx={{ ...typography.bodyLarge }}>
                Add security recommendation{" "}
                <span style={{ color: ColorsConstants.primaryGray50 }}>
                  ("For your security, do not share this code.")
                </span>
              </Typography>
            }
          />
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
            <FormControlLabel
              control={<Switch checked={expiryEnabled} onChange={(e) => setExpiryEnabled(e.target.checked)} />}
              label={<Typography sx={{ ...typography.bodyLarge }}>Add code expiry time</Typography>}
            />
            {expiryEnabled && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="code-expiry-label">Expires in</InputLabel>
                <Select
                  labelId="code-expiry-label"
                  label="Expires in"
                  value={expiryMinutes}
                  onChange={(e) => setExpiryMinutes(e.target.value)}
                >
                  {CODE_EXPIRY_OPTIONS.map((minutes) => (
                    <MenuItem key={minutes} value={minutes}>
                      {minutes} minute{minutes === 1 ? "" : "s"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderDelivery = () => (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ ...typography.h1 }}>
            Code delivery setup
          </Typography>
          <RadioGroup value={otpType} onChange={(e) => setOtpType(e.target.value)}>
            {DELIVERY_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                sx={{ alignItems: "flex-start", mb: 1 }}
                control={
                  <Radio sx={{ pt: 0.5, "&.Mui-checked": { color: ColorsConstants.primaryBlue100 } }} />
                }
                label={
                  <Box>
                    <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>{option.label}</Typography>
                    <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray60 }}>
                      {option.description}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>

          {otpType === "ZERO_TAP" && (
            <Box sx={{ border: `1px solid ${ColorsConstants.secondaryGray}`, borderRadius: "8px", p: 1.5 }}>
              <FormControlLabel
                sx={{ alignItems: "flex-start" }}
                control={
                  <Checkbox
                    sx={{ pt: 0.5 }}
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ ...typography.bodyLarge }}>
                    By selecting zero-tap, I understand that use of zero-tap authentication is
                    subject to the{" "}
                    <Link href={BUSINESS_TERMS_URL} target="_blank" rel="noopener noreferrer">
                      WhatsApp Business Terms of Service
                    </Link>
                    .
                  </Typography>
                }
              />
              {!termsAccepted && (
                <Typography variant="body2" color="error" sx={{ ml: 4 }}>
                  Tick this box to submit the template.
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderApps = () => (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ ...typography.h1 }}>
              App setup
            </Typography>
            <Typography variant="body2" sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryGray60 }}>
              Add the Android apps that will receive the code. You can add up to {MAX_APPS}.
            </Typography>
          </Box>

          {apps.map((app, index) => {
            const errors = appErrors[index];
            const shownErrors = ["package_name", "signature_hash"]
              .filter((key) => errors[key] && touched[`${app.id}.${key}`])
              .map((key) => errors[key]);
            return (
              <Stack key={app.id} spacing={1}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
                  <TextField
                    size="small"
                    sx={{ flex: 2 }}
                    label="Package name"
                    value={app.package_name}
                    onChange={(e) => updateApp(app.id, "package_name", e.target.value)}
                    onBlur={() => touchApp(app.id, "package_name")}
                    inputProps={{ maxLength: PACKAGE_NAME_MAX }}
                    helperText={`${app.package_name.length}/${PACKAGE_NAME_MAX}`}
                    FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
                  />
                  <TextField
                    size="small"
                    sx={{ flex: 1 }}
                    label="App signature hash"
                    value={app.signature_hash}
                    onChange={(e) => updateApp(app.id, "signature_hash", e.target.value)}
                    onBlur={() => touchApp(app.id, "signature_hash")}
                    inputProps={{ maxLength: SIGNATURE_HASH_LENGTH }}
                    helperText={`${app.signature_hash.length}/${SIGNATURE_HASH_LENGTH}`}
                    FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
                  />
                  {index > 0 && (
                    <IconButton
                      aria-label="Remove app"
                      onClick={() => setApps(apps.filter((a) => a.id !== app.id))}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
                {shownErrors.length > 0 && (
                  <Box sx={errorBoxSx}>
                    {shownErrors.map((message) => (
                      <div key={message}>{message}</div>
                    ))}
                  </Box>
                )}
              </Stack>
            );
          })}

          <Box>
            <Button
              size="small"
              sx={linkButtonSx}
              disabled={apps.length >= MAX_APPS}
              onClick={() => setApps([...apps, newApp()])}
            >
              + Add another app
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const deliveryLabel = DELIVERY_OPTIONS.find((o) => o.value === otpType)?.label;

  return (
    <Stack direction={{ xs: "column", md: "row" }} sx={{ flexDirection: { xs: "column", md: "row" } }} spacing={2}>
      <Box sx={{ flex: 1 }}>
        <Stack spacing={4}>
          {selected ? renderBody() : renderPicker()}
          {renderContentOptions()}
          {renderDelivery()}
          {otpType !== "COPY_CODE" && renderApps()}
        </Stack>
      </Box>

      {/* Preview */}
      <Box>
        <MessageNode
          templateName={templateName}
          language={language}
          headerType="none"
          headerText=""
          headerFile={null}
          sampleBodyFields={[]}
          sampleHeaderFields={[]}
          {...(selected
            ? buildAuthPreview({
                body: selected.body,
                sampleBodyFields,
                addSecurity,
                expiryMinutes: expiryEnabled ? expiryMinutes : 0,
                otpType,
              })
            : { body: "", footer: "", allButtons: [] })}
          showSampleValues={true}
          mode="edit"
        />
        <Typography
          variant="body2"
          sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray50, mt: 1, textAlign: "center" }}
        >
          Authentication · {templateName || "template_name"} · {deliveryLabel}
        </Typography>
      </Box>
    </Stack>
  );
};

export default AuthTemplateEditor;
